import React, { useMemo } from "react";
import { STATUS_OPTIONS, statusStyle } from "../../studentStyles";
import { normalizeStudentStatus } from "../../../../../utils/studentLifecycle";
import "./StudentInfoTab.css";

function normalizeIntakeLabel(label) {
  return String(label || "")
    .trim()
    .replace(/\s*:\s*$/, "")
    .replace(/\s+/g, " ");
}

function formatIntakeValue(value) {
  if (value == null) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object" && typeof value.seconds === "number") {
    const d = new Date(value.seconds * 1000);
    return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function Field({ label, children, wide = false }) {
  return (
    <label className={`info-field ${wide ? "info-field--wide" : ""}`}>
      <span className="info-field__label">{label}</span>
      {children}
    </label>
  );
}

function Section({ title, children, wide = false }) {
  return (
    <section className={`info-section ${wide ? "info-section--wide" : ""}`}>
      <div className="info-section__title">{title}</div>
      {children}
    </section>
  );
}

export default function StudentInfoTab({ student, setField, permissions }) {
  const readOnly = permissions ? !permissions.editInfo : false;
  const status =
    STATUS_OPTIONS.find(
      (opt) => opt.toLowerCase() === String(student?.status || "").trim().toLowerCase()
    ) || normalizeStudentStatus(student?.status);

  const intakeEntries = useMemo(() => {
    const data = student?.customFields;
    if (!data || typeof data !== "object") return [];

    return Object.entries(data)
      .map(([k, v]) => ({ label: normalizeIntakeLabel(k), value: formatIntakeValue(v) }))
      .filter((x) => x.label && x.value)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [student?.customFields]);

  return (
    <div className={`student-info-tab ${readOnly ? "is-readonly" : ""}`}>
      <div className="student-info-tab__grid">
        <Section title="Student">
          <div className="info-fields">
            <Field label="Full Name" wide>
              <input
                value={student?.name || ""}
                onChange={(e) => setField("name", e.target.value)}
                disabled={readOnly}
              />
            </Field>
            <Field label="Grade">
              <input
                type="number"
                value={student?.grade ?? ""}
                onChange={(e) => setField("grade", e.target.value)}
                disabled={readOnly}
              />
            </Field>
            <Field label="Status">
              <select
                style={statusStyle(status)}
                value={status}
                onChange={(e) => setField("status", e.target.value)}
                disabled={readOnly}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Date of Birth">
              <input
                type="date"
                value={student?.dob || ""}
                onChange={(e) => setField("dob", e.target.value)}
                disabled={readOnly}
              />
            </Field>
            <Field label="School">
              <input
                value={student?.school || ""}
                onChange={(e) => setField("school", e.target.value)}
                disabled={readOnly}
              />
            </Field>
            <Field label="First Session">
              <input
                type="date"
                value={student?.startDate || ""}
                onChange={(e) => setField("startDate", e.target.value)}
                disabled={readOnly}
              />
            </Field>
            <Field label="Last Session">
              <input
                type="date"
                value={student?.endDate || ""}
                onChange={(e) => setField("endDate", e.target.value)}
                disabled={readOnly}
              />
            </Field>
          </div>
        </Section>

        <Section title="Contact">
          <div className="info-fields">
            <Field label="Email">
              <input
                value={student?.email || ""}
                onChange={(e) => setField("email", e.target.value)}
                disabled={readOnly}
              />
            </Field>
            <Field label="Phone">
              <input
                value={student?.phone || ""}
                onChange={(e) => setField("phone", e.target.value)}
                disabled={readOnly}
              />
            </Field>
            <Field label="Home Address" wide>
              <input
                value={student?.homeAddress || ""}
                onChange={(e) => setField("homeAddress", e.target.value)}
                disabled={readOnly}
              />
            </Field>
          </div>
        </Section>

        <Section title="Parent / Guardian">
          <div className="info-fields">
            <Field label="Parent Name" wide>
              <input
                value={student?.parentName || ""}
                onChange={(e) => setField("parentName", e.target.value)}
                disabled={readOnly}
              />
            </Field>
            <Field label="Parent Email">
              <input
                value={student?.parentEmail || ""}
                onChange={(e) => setField("parentEmail", e.target.value)}
                disabled={readOnly}
              />
            </Field>
            <Field label="Parent Phone">
              <input
                value={student?.parentPhone || ""}
                onChange={(e) => setField("parentPhone", e.target.value)}
                disabled={readOnly}
              />
            </Field>
          </div>
        </Section>

        <Section title="Notes">
          <textarea
            className="info-note"
            value={student?.note || ""}
            onChange={(e) => setField("note", e.target.value)}
            placeholder={readOnly ? "No notes." : "Write notes here…"}
            disabled={readOnly}
          />
        </Section>
      </div>

      {intakeEntries.length > 0 && (
        <Section title="Intake Information" wide>
          <div className="info-intake">
            {intakeEntries.map((entry) => (
              <div key={entry.label} className="info-intake__row">
                <div className="info-intake__label">{entry.label}</div>
                <div className="info-intake__value">{entry.value}</div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
